import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  addCourseDocumentRequirement,
  listCourseDocumentRequirements,
  removeCourseDocumentRequirement,
} from '../../api/academic';
import { listLookup } from '../../api/masterData';

type RequirementLevel = 'NONE' | 'OPTIONAL' | 'REQUIRED';

interface CourseDocumentRequirementsDialogProps {
  courseId: number;
  courseName: string;
  canEdit: boolean;
  onClose: () => void;
}

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

/** Admissions document-requirement correction (Phase 60) — the single
 * place an admin defines "University → Course → required documents".
 * Reuses the existing CourseDocumentRequirement CRUD (already built, never
 * previously wired to any UI) and the existing global DocumentType
 * catalog — never a second, duplicate document-type list. Admissions and
 * the student profile's checklist both read the rows this dialog writes. */
export const CourseDocumentRequirementsDialog = ({
  courseId,
  courseName,
  canEdit,
  onClose,
}: CourseDocumentRequirementsDialogProps) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const requirementsQueryKey = ['course-document-requirements', courseId];
  const requirementsQuery = useQuery({
    queryKey: requirementsQueryKey,
    queryFn: () => listCourseDocumentRequirements(courseId),
  });
  const documentTypesQuery = useQuery({
    queryKey: ['lookup', '/master-data/document-types'],
    queryFn: () => listLookup('/master-data/document-types', { activeOnly: true }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: requirementsQueryKey });

  const addMutation = useMutation({
    mutationFn: ({ documentTypeId, isMandatory }: { documentTypeId: number; isMandatory: boolean }) =>
      addCourseDocumentRequirement(courseId, documentTypeId, isMandatory),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err: unknown) => setError(extractErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (requirementId: number) => removeCourseDocumentRequirement(requirementId),
    onError: (err: unknown) => setError(extractErrorMessage(err)),
  });

  const requirementByTypeId = new Map((requirementsQuery.data ?? []).map((r) => [r.documentType.id, r]));

  const levelFor = (documentTypeId: number): RequirementLevel => {
    const req = requirementByTypeId.get(documentTypeId);
    if (!req) return 'NONE';
    return req.isMandatory ? 'REQUIRED' : 'OPTIONAL';
  };

  // No dedicated "update mandatory flag" endpoint exists on this join row
  // (by design — avoid unnecessary API surface for a rarely-changed
  // toggle) — switching Required <-> Optional removes the old row and
  // adds the new one, which is exactly what the CourseDocumentRequirement
  // unique constraint (courseId+documentTypeId) already allows once the
  // first is gone.
  const handleChange = (documentTypeId: number, next: RequirementLevel | null) => {
    if (!next) return;
    setError(null);
    const existing = requirementByTypeId.get(documentTypeId);
    if (next === 'NONE') {
      if (existing) removeMutation.mutate(existing.id, { onSuccess: invalidate });
      return;
    }
    const isMandatory = next === 'REQUIRED';
    if (existing) {
      if (existing.isMandatory === isMandatory) return;
      removeMutation.mutate(existing.id, {
        onSuccess: () => {
          invalidate();
          addMutation.mutate({ documentTypeId, isMandatory });
        },
      });
      return;
    }
    addMutation.mutate({ documentTypeId, isMandatory });
  };

  const busy = addMutation.isPending || removeMutation.isPending;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Document Requirements — {courseName}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which documents students applying to this program must submit. "Required" documents drive the
          Admissions completion count and the student's document checklist; "Optional" ones are tracked but never
          block completion.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document Type</TableCell>
                <TableCell align="right">Requirement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentTypesQuery.data?.map((t) => {
                const documentTypeId = t.id;
                const level = levelFor(documentTypeId);
                return (
                  <TableRow key={documentTypeId}>
                    <TableCell>{String(t.name)}</TableCell>
                    <TableCell align="right">
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={level}
                        disabled={!canEdit || busy}
                        onChange={(_e, next) => handleChange(documentTypeId, next)}
                      >
                        <ToggleButton value="NONE">Not required</ToggleButton>
                        <ToggleButton value="OPTIONAL">Optional</ToggleButton>
                        <ToggleButton value="REQUIRED">Required</ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
